import React, { useCallback, useEffect } from 'react';
import { useProviderHelpers } from './ProviderHelpers';

const Context = React.createContext();

// Cache lookup values outside component to persist across instances
let cachedLookupValues = null;

export const FranchiseFineProvider = ({ children }) => {
    const { data, execute, clearData } = useProviderHelpers();

    // Cache lookup values when successfully fetched
    useEffect(() => {
        if (data && data.scope === 'list_lookupvalues' && data.status === 'success' && !cachedLookupValues) {
            cachedLookupValues = data.body;
        }
    }, [data]);

    const selectRecord = useCallback(async (props) => {
        await execute({ scope: 'select', url: `FranchiseFine/rules/${props.fineId}`, method: 'GET', secure: true });
    }, [execute]);

    const newRecord = useCallback(async (props) => {
        await execute({ scope: 'new', url: `FranchiseFine/GetBlankFine`, method: 'GET', secure: true });
    }, [execute]);

    const createRecord = useCallback(async (props) => {
        await execute({ scope: 'create', url: `FranchiseFine/rules`, method: 'POST', payload: props, secure: true });
    }, [execute]);

    const updateRecord = useCallback(async (props) => {
        await execute({ scope: 'edit', url: `FranchiseFine/rules`, method: 'PUT', payload: props, secure: true });
    }, [execute]);

    const deleteRecord = useCallback(async (props) => {
        await execute({ scope: 'delete', url: `FranchiseFine/rules/${props.fineId}`, method: 'DELETE', secure: true });
    }, [execute]);

    const listRecords = useCallback(async (props) => {
        await execute({ scope: 'list', url: `FranchiseFine/rules`, method: 'GET', secure: true });
    }, [execute]);

    const listLookupValues = useCallback(async (props) => {
        if (cachedLookupValues) {
            // Make cached response asynchronous to match network behavior
            await new Promise(resolve => {
                setTimeout(() => {
                    execute({
                        scope: 'list_lookupvalues',
                        url: '',
                        method: 'GET',
                        secure: true,
                        cached: true,
                        cachedData: cachedLookupValues
                    });
                    resolve();
                }, 0);
            });
            return;
        }

        await execute({ scope: 'list_lookupvalues', url: `FranchiseFine/GetRuleLookupValues`, method: 'GET', secure: true });
    }, [execute]);

    const clearLookupCache = useCallback(() => {
        cachedLookupValues = null;
    }, []);

    const value = { listRecords, newRecord, createRecord, selectRecord, updateRecord, deleteRecord, listLookupValues, data, clearData, clearLookupCache };

    return (
        <Context.Provider value={value}>
            {children}
        </Context.Provider>
    );
};

export const useFranchiseFineStore = () => React.useContext(Context);